import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { Button, Checkbox } from 'antd';
import { TbCircle } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DragHandle, DraggableProvidedDragHandleProps } from '@/shared/components/drag-and-drop';
import CompactCollapsible from '@/shared/components/layouts/sections/CompactCollapsible';
import CompactCircularProgress from '@/shared/components/progress/CompactCircularProgress';

import LifecycleTaskList from '../LifecycleTaskList';
import { lifecycleProgressSummary } from '../progress';
import type { LifecycleStagePreview } from '../types';

interface LifecycleStageCollapsibleProps {
    actions?: (controls: { open: boolean; setOpen: (open: boolean) => void }) => ReactNode;
    dragDisabled?: boolean;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    edgeToEdge?: boolean;
    onSelectedChange?: (selected: boolean) => void;
    selected?: boolean;
    stage: LifecycleStagePreview;
}

const LifecycleStageCollapsible: FC<LifecycleStageCollapsibleProps> = ({
    actions,
    dragDisabled = false,
    dragHandleProps,
    edgeToEdge = false,
    onSelectedChange,
    selected = false,
    stage,
}) => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(stage.defaultOpen ?? false);
    const [visibleTaskCount, setVisibleTaskCount] = useState(2);
    const progress = lifecycleProgressSummary(stage);
    const stageColor = stage.color ?? token.colorPrimary;
    const visibleTasks = stage.tasks.slice(0, visibleTaskCount);
    const remainingTaskCount = Math.max(0, stage.tasks.length - visibleTaskCount);

    return (
        <CompactCollapsible
            bordered
            contentStyle={{ padding: 0 }}
            edgeToEdge={edgeToEdge}
            headerPrefix={
                <DragHandle
                    disabled={dragDisabled}
                    dragHandleProps={dragHandleProps}
                    label={`Reorder ${stage.label} Task Group`}
                    style={{ height: '1.5rem', minWidth: '1.5rem' }}
                />
            }
            headerPrefixHoverOnly
            headerSelection={
                onSelectedChange ? (
                    <Checkbox
                        aria-label={`Select ${stage.label} Task Group`}
                        checked={selected}
                        onChange={(event) => onSelectedChange(event.target.checked)}
                    />
                ) : null
            }
            headerSelectionActive={selected}
            id={`lifecycle-stage-${stage.id}`}
            headerExtra={
                <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.75rem' }}>
                    <span style={{ alignItems: 'center', display: 'inline-flex', flex: 'none', gap: '0.375rem' }}>
                        <CompactCircularProgress percent={progress.percent} size={14} />
                        <small style={{ color: token.colorTextTertiary }}>
                            {progress.totalTasks} {progress.totalTasks === 1 ? 'Task' : 'Tasks'}
                        </small>
                    </span>
                    {actions?.({ open, setOpen })}
                </span>
            }
            label={
                <span style={{ display: 'block', minWidth: 0, width: '100%' }}>
                    <span
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '0.5rem',
                            justifyContent: 'space-between',
                        }}
                    >
                        <span style={{ alignItems: 'center', display: 'flex', gap: '0.375rem', minWidth: 0 }}>
                            <TbCircle
                                aria-hidden
                                color={stageColor}
                                fill={stageColor}
                                fillOpacity={0.2}
                                size='0.75rem'
                                strokeWidth={2}
                            />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {stage.label}
                            </span>
                        </span>
                    </span>
                    {stage.description ? (
                        <small
                            style={{
                                color: token.colorTextTertiary,
                                display: '-webkit-box',
                                fontWeight: 400,
                                lineHeight: '1rem',
                                marginLeft: '-1.5rem',
                                marginTop: '0.5rem',
                                overflow: 'hidden',
                                width: 'calc(100% + 1.5rem)',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                            }}
                        >
                            {stage.description}
                        </small>
                    ) : null}
                </span>
            }
            open={open}
            sectionStyle={{
                background: token.colorBgContainer,
                ...(!edgeToEdge ? { border: `1px solid ${token.colorBorderSecondary}` } : undefined),
            }}
            headerStyle={{
                alignItems: stage.description ? 'flex-start' : 'center',
                paddingBlock: stage.description ? '0.5rem' : '0.25rem',
            }}
            onOpenChange={setOpen}
        >
            <LifecycleTaskList
                emptyDescription='Tasks assigned to this stage will appear here.'
                emptyHeight='7rem'
                emptyLabel='No Tasks in This Stage'
                tasks={visibleTasks}
            />
            {remainingTaskCount ? (
                <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, paddingBlock: '0.5rem' }}>
                    <Button
                        aria-label={`See ${remainingTaskCount} more tasks in ${stage.label}`}
                        block
                        size='small'
                        type='link'
                        onClick={() => setVisibleTaskCount(stage.tasks.length)}
                    >
                        See More Tasks
                    </Button>
                </div>
            ) : null}
        </CompactCollapsible>
    );
};

export default LifecycleStageCollapsible;
